import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Search, X, ChevronDown } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import { useAppSelector } from '@/store/hooks'

interface SearchableMemberSelectProps {
  selectedMemberId: string | null
  selectedMemberName: string | null
  onSelect: (memberId: string, memberName: string, memberEmail: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
  workspaceId: string
  // 외부에서 멤버 데이터를 전달받을 수 있음 (성능 최적화)
  members?: OrgMemberResponse[]
  isLoading?: boolean
  membersError?: string | null
  // 검색 관련 설정
  searchPlaceholder?: string
  minSearchLength?: number
  maxDisplayResults?: number
}

// Custom hook for workspace member data loading
const useWorkspaceMembers = (workspaceId: string, enabled: boolean = true) => {
  const [members, setMembers] = useState<OrgMemberResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  const fetchMembers = useCallback(async () => {
    if (!workspaceId || !selectedWorkspace?.org_id || !enabled) return

    setIsLoading(true)
    setError(null)

    try {
      const response: PaginatedResponse = await OrgMemberService.appsOrganizationViewsListOrgMembers({
        orgId: selectedWorkspace.org_id,
        workspaceId,
        page: 1,
        perPage: 1000 // 더 많은 멤버 로드 지원
      })

      const orgMembers = response.data as OrgMemberResponse[]
      const workspaceMembers: OrgMemberResponse[] = orgMembers
        .filter(member => member.workspaces?.some(ws => ws.id === workspaceId))
        .map(member => ({
          id: member.id,
          account_id: member.account_id,
          account_name: member.account_name,
          account_email: member.account_email,
          account_phone: member.account_phone || '',
          workspace_id: workspaceId,
          workspace_name: member.workspaces?.find(ws => ws.id === workspaceId)?.name || ''
        }))
        .sort((a, b) => a.account_name.localeCompare(b.account_name)) // 이름 순으로 정렬

      setMembers(workspaceMembers)
    } catch (err) {
      console.error('Failed to fetch workspace members:', err)
      setError('멤버 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId, selectedWorkspace?.org_id, enabled])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  return { members, isLoading, error, refetch: fetchMembers }
}

const SearchableMemberSelect: React.FC<SearchableMemberSelectProps> = ({
  selectedMemberId,
  selectedMemberName,
  onSelect,
  disabled = false,
  error = null,
  placeholder = "멤버를 선택하세요",
  workspaceId,
  // 외부에서 전달된 멤버 데이터
  members: externalMembers,
  isLoading: externalIsLoading,
  membersError: externalMembersError,
  // 검색 설정
  searchPlaceholder = "이름으로 검색...",
  minSearchLength = 1,
  maxDisplayResults = 10
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // 외부 데이터가 있으면 사용하고, 없으면 내부 hook 사용
  const internalData = useWorkspaceMembers(workspaceId, !externalMembers)

  const members = externalMembers || internalData.members
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : internalData.isLoading
  const fetchError = externalMembersError || internalData.error

  // 검색 필터링된 멤버 목록
  const filteredMembers = useMemo(() => {
    if (!searchTerm || searchTerm.length < minSearchLength) {
      return members.slice(0, maxDisplayResults)
    }

    const searchLower = searchTerm.toLowerCase()
    const filtered = members.filter(member =>
      member.account_name.toLowerCase().includes(searchLower) ||
      member.account_email.toLowerCase().includes(searchLower)
    )

    return filtered.slice(0, maxDisplayResults)
  }, [members, searchTerm, minSearchLength, maxDisplayResults])

  // 검색어 하이라이팅 함수
  const highlightMatch = (text: string, searchTerm: string) => {
    if (!searchTerm || searchTerm.length < minSearchLength) return text

    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'))
    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={index} style={{ backgroundColor: designTokens.colors.primary.light, fontWeight: 600 }}>
          {part}
        </span>
      ) : part
    )
  }

  const handleSelect = (member: OrgMemberResponse) => {
    onSelect(member.id, member.account_name, member.account_email)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect('', '', '')
    setSearchTerm('')
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true)
      // 드롭다운 열릴 때 검색 입력에 포커스
      setTimeout(() => {
        const searchInput = document.getElementById('member-search-input')
        searchInput?.focus()
      }, 100)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setSearchTerm('')
  }

  return (
    <div className="relative">
      {/* Selection Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: error ? designTokens.colors.error.DEFAULT : (isOpen ? designTokens.colors.primary.DEFAULT : designTokens.colors.gray[1]),
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          color: selectedMemberName ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <span>{selectedMemberName || placeholder}</span>
          <div className="flex items-center gap-2">
            {selectedMemberName && !disabled && (
              <span
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                style={{ fontSize: '14px' }}
              >
                ✕
              </span>
            )}
            <ChevronDown
              className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              style={{ color: designTokens.colors.gray[3] }}
            />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border-2 rounded-lg shadow-lg max-h-80 flex flex-col"
          style={{
            borderColor: designTokens.colors.gray[1]
          }}
        >
          {/* Search Input */}
          <div className="p-3 border-b" style={{ borderBottomColor: designTokens.colors.gray[1] }}>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: designTokens.colors.gray[3] }}
              />
              <input
                id="member-search-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-8 py-2 border rounded-md focus:outline-none focus:ring-2"
                style={{
                  borderColor: designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  focusRingColor: designTokens.colors.primary.light
                }}
                autoComplete="off"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Results List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div
                className="p-4 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[4]
                }}
              >
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                  멤버 목록을 불러오는 중...
                </div>
              </div>
            ) : fetchError ? (
              <div
                className="p-4 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.error.DEFAULT
                }}
              >
                {fetchError}
              </div>
            ) : filteredMembers.length === 0 ? (
              <div
                className="p-4 text-center"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[4]
                }}
              >
                {searchTerm ? (
                  <>
                    '<strong>{searchTerm}</strong>'에 대한 검색 결과가 없습니다.
                  </>
                ) : (
                  '워크스페이스 멤버가 없습니다.'
                )}
              </div>
            ) : (
              <>
                {/* Results Info */}
                {searchTerm && searchTerm.length >= minSearchLength && (
                  <div
                    className="px-4 py-2 text-xs border-b"
                    style={{
                      backgroundColor: designTokens.colors.gray[0],
                      borderBottomColor: designTokens.colors.gray[1],
                      color: designTokens.colors.gray[4]
                    }}
                  >
                    {filteredMembers.length}개의 검색 결과
                    {filteredMembers.length === maxDisplayResults && ' (처음 ' + maxDisplayResults + '개만 표시)'}
                  </div>
                )}

                {/* Member List */}
                {filteredMembers.map((member) => {
                  const isSelected = member.id === selectedMemberId
                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => handleSelect(member)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        backgroundColor: isSelected ? designTokens.colors.primary.light + '20' : 'transparent'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <div
                            className="font-medium"
                            style={{
                              fontSize: '15px',
                              color: designTokens.colors.gray[6]
                            }}
                          >
                            {highlightMatch(member.account_name, searchTerm)}
                            {isSelected && (
                              <span
                                className="ml-2 text-xs px-2 py-0.5 rounded"
                                style={{
                                  backgroundColor: designTokens.colors.primary.DEFAULT,
                                  color: designTokens.colors.white
                                }}
                              >
                                선택됨
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: '13px',
                              color: designTokens.colors.gray[4]
                            }}
                          >
                            {highlightMatch(member.account_email, searchTerm)}
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })}

                {/* Load More Hint */}
                {members.length > maxDisplayResults && filteredMembers.length === maxDisplayResults && (
                  <div
                    className="px-4 py-2 text-xs text-center"
                    style={{
                      color: designTokens.colors.gray[4],
                      backgroundColor: designTokens.colors.gray[0]
                    }}
                  >
                    더 많은 결과를 보려면 검색어를 입력하세요
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="mt-1 text-sm"
          style={{
            color: designTokens.colors.error.DEFAULT,
            fontFamily: designTokens.typography.fontFamily.sans[0]
          }}
        >
          {error}
        </div>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={handleClose}
        />
      )}
    </div>
  )
}

export default SearchableMemberSelect